import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '@aws-amplify/ui-react/styles.css';
import './verification.css';

import PrivacyStep from './steps/PrivacyStep';
import DocumentCaptureStep from './steps/DocumentCaptureStep';
import SelfieWithLivenessStep from './steps/SelfieWithLivenessStep';
import ResultStep from './steps/ResultStep';
import rekognitionGateway from '../../services/rekognitionGateway';
import { logVerificationError, resolveRequireDocumentBack } from './verificationUtils';

/**
 * VerificationModal - Main orchestrator for biometric identity verification
 * 
 * Manages 4-step verification flow:
 * 1. Privacy notice
 * 2. Document ID capture (front + back)
 * 3. Selfie capture with liveness detection
 * 4. Result display
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Called when user cancels or closes modal
 * @param {Function} props.onSuccess - Called when verification succeeds with evidence
 * @param {Object} props.signer - Current signer information
 * @param {string} props.signer.name - Signer name
 * @param {string} props.signer.email - Signer email
 * @param {string} props.signer.objectId - Signer Parse object ID
 * @param {string} props.documentId - Document being signed
 * @param {Object} props.verificationConfig - Verification configuration
 * @param {number} props.verificationConfig.threshold - Similarity threshold (0-100)
 * @param {boolean} props.verificationConfig.requireDocument - Whether ID capture is required
 * @param {boolean} props.verificationConfig.requireDocumentBack - Whether back side of ID is required (default: true)
 */
function VerificationModal({
  isOpen,
  onClose,
  onSuccess,
  signer,
  documentId,
  verificationConfig = { threshold: 85, requireDocument: true, requireDocumentBack: true }
}) {
  const { t, i18n } = useTranslation();
  const isDocumentBackRequired = resolveRequireDocumentBack(verificationConfig?.requireDocumentBack, true);

  // Step state machine: privacy -> document -> selfie -> result
  const [step, setStep] = useState('privacy');

  // Captured images
  const [documentFront, setDocumentFront] = useState(null);
  const [documentBack, setDocumentBack] = useState(null);
  const [selfie, setSelfie] = useState(null);

  // Processing state
  const [status, setStatus] = useState('idle'); // idle | processing | success | error
  const [message, setMessage] = useState('');

  // Verification result
  const [result, setResult] = useState(null);
  const modalContentRef = useRef(null);

  /**
   * Handles modal close - resets all state
   */
  const handleClose = useCallback(() => {
    // Reset all state on close
    setStep('privacy');
    setDocumentFront(null);
    setDocumentBack(null);
    setSelfie(null);
    setStatus('idle');
    setMessage('');
    setResult(null);
    onClose();
  }, [onClose]);

  /**
   * Handles privacy step completion
   */
  const handlePrivacyContinue = useCallback(() => {
    setStep('document');
  }, []);

  /**
   * Handles document capture completion
   */
  const handleDocumentCapture = useCallback((frontImage, backImage) => {
    setDocumentFront(frontImage);
    setDocumentBack(backImage);
    setStatus('idle');
    setMessage('');
    setStep('selfie');
  }, []);

  const formatExpiryDate = useCallback(
    (value) => {
      if (!value) {
        return '';
      }
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return '';
      }
      return parsed.toLocaleDateString(i18n?.language || 'es');
    },
    [i18n?.language]
  );

  const validateCapturedDocumentExpiry = useCallback(
    async (frontImage) => {
      if (!frontImage?.base64) {
        throw new Error(
          t('verification.document-front-required', {
            defaultValue: 'Debes capturar el frente del documento.'
          })
        );
      }

      setStatus('processing');
      setMessage(
        t('verification.checking-document-expiry', {
          defaultValue: 'Verificando vigencia del documento...'
        })
      );

      const expiryValidation = await rekognitionGateway.validateDocumentExpiry(frontImage.base64);

      // If expiry could not be determined (OCR didn't find expiry keywords),
      // let the user continue — the real identity check is face comparison.
      // Only block if the document is definitively expired.
      if (expiryValidation?.checked && expiryValidation?.isExpired) {
        const expiryDateLabel = formatExpiryDate(expiryValidation.expiryDate);
        throw new Error(
          expiryDateLabel
            ? t('verification.expired-document-identity', {
                defaultValue: 'El documento de identidad esta vencido ({{date}}).',
                date: expiryDateLabel
              })
            : t('verification.expired-document-identity-no-date', {
                defaultValue: 'El documento de identidad esta vencido.'
              })
        );
      }

      setStatus('idle');
      setMessage('');
      return expiryValidation;
    },
    [formatExpiryDate, t]
  );

  /**
   * Handles selfie capture and triggers verification
   */
  const handleSelfieCapture = useCallback(
    async (selfieImage) => {
      setSelfie(selfieImage);
      setStatus('processing');
      setMessage(
        t('verification.processing-message', {
          defaultValue: 'Verificando tu identidad...'
        })
      );

      try {
        // Call AWS Rekognition via Parse Cloud function
        const comparisonResult = await rekognitionGateway.compareFaces(
          documentFront.base64,
          selfieImage.base64,
          verificationConfig.threshold
        );

        console.log('=== COMPARISON RESULT FROM BACKEND ===');
        console.log('Full response:', comparisonResult);
        console.log('livenessScore:', comparisonResult.livenessScore);
        console.log('livenessConfidence:', comparisonResult.livenessConfidence);

        // Extract results
        const faceMatch = comparisonResult.FaceMatches?.[0];
        const similarity = faceMatch?.Similarity || 0;
        const livenessScore = comparisonResult.livenessScore || 0;
        const livenessConfidence = comparisonResult.livenessConfidence || 'LOW';
        
        console.log('Extracted similarity:', similarity);
        console.log('Extracted livenessScore:', livenessScore);
        console.log('Extracted livenessConfidence:', livenessConfidence);

        // Validate against thresholds
        const similarityValid = similarity >= verificationConfig.threshold;
        const livenessValid = livenessScore >= 70; // Minimum liveness threshold
        const isValid = similarityValid && livenessValid;

        // Build verification evidence
        const evidence = {
          provider: 'rekognition',
          similarity,
          matchedPerson: faceMatch || null,
          livenessScore,
          livenessConfidence,
          evidence: {
            documentFront: {
              base64: documentFront.base64,
              name: documentFront.name,
              type: documentFront.type,
              size: documentFront.size,
              capturedAt: documentFront.capturedAt
            },
            document: {
              base64: documentFront.base64,
              name: documentFront.name,
              type: documentFront.type,
              size: documentFront.size,
              capturedAt: documentFront.capturedAt
            },
            ...(documentBack && {
              documentBack: {
                base64: documentBack.base64,
                name: documentBack.name,
                type: documentBack.type,
                size: documentBack.size,
                capturedAt: documentBack.capturedAt
              }
            }),
            selfie: {
              base64: selfieImage.base64,
              name: selfieImage.name,
              type: selfieImage.type,
              size: selfieImage.size,
              capturedAt: selfieImage.capturedAt
            },
            compareFaceCapture: {
              base64: selfieImage.base64,
              name: selfieImage.name,
              type: selfieImage.type,
              size: selfieImage.size,
              capturedAt: selfieImage.capturedAt
            },
            livenessSession: {
              sessionId: `session-${Date.now()}`,
              auditImages: []
            }
          },
          verifiedAt: Date.now(),
          signer: {
            name: signer.name,
            email: signer.email,
            objectId: signer.objectId
          },
          documentId
        };

        // Set result
        setResult({
          isValid,
          similarity,
          livenessScore,
          livenessConfidence,
          payload: evidence
        });

        if (isValid) {
          setStatus('success');
          setMessage(
            t('verification.success-message', {
              defaultValue: 'Verificación exitosa!'
            })
          );
        } else {
          setStatus('error');
          if (!similarityValid) {
            setMessage(
              t('verification.similarity-failed', {
                defaultValue: 'La similitud facial está por debajo del umbral requerido.'
              })
            );
          } else if (!livenessValid) {
            setMessage(
              t('verification.liveness-failed', {
                defaultValue: 'La verificación de vida falló. Por favor asegúrate de estar en un área bien iluminada.'
              })
            );
          }
        }

        // Move to result step
        setStep('result');
      } catch (error) {
        logVerificationError(error, {
          step: 'selfie',
          documentId,
          signer
        });

        setStatus('error');
        setMessage(
          error.message ||
            t('verification.error-generic', {
              defaultValue: 'La verificación falló. Por favor intenta de nuevo.'
            })
        );

        // Set error result
        setResult({
          isValid: false,
          similarity: 0,
          livenessScore: 0,
          livenessConfidence: 'LOW',
          payload: null
        });

        setStep('result');
      }
    },
    [documentFront, documentBack, verificationConfig, signer, documentId, t]
  );

  /**
   * Handles successful verification - saves evidence and calls onSuccess
   */
  const handleResultContinue = useCallback(async () => {
    if (!result?.isValid || !result?.payload) {
      return;
    }

    try {
      setStatus('processing');
      setMessage(
        t('verification.saving-evidence', {
          defaultValue: 'Guardando evidencia de verificación...'
        })
      );

      // Save verification evidence to Parse Server
      await rekognitionGateway.saveVerification({
        docId: documentId,
        signerObjId: signer.objectId,
        ...result.payload
      });

      // Call success callback with evidence
      onSuccess(result.payload);

      // Close modal
      handleClose();
    } catch (error) {
      logVerificationError(error, {
        step: 'save-evidence',
        documentId,
        signer
      });

      setStatus('error');
      setMessage(
        error.message ||
          t('verification.save-error', {
            defaultValue: 'Error al guardar la evidencia de verificación. Por favor intenta de nuevo.'
          })
      );
    }
  }, [result, documentId, signer, onSuccess, handleClose, t]);

  /**
   * Handles retry - resets to privacy step
   */
  const handleRetry = useCallback(() => {
    setStep('privacy');
    setDocumentFront(null);
    setDocumentBack(null);
    setSelfie(null);
    setStatus('idle');
    setMessage('');
    setResult(null);
  }, []);

  /**
   * Handles contact support
   */
  const handleContactSupport = useCallback(() => {
    // TODO: Implement support contact mechanism
    alert(
      t('verification.support-message', {
        defaultValue:
          'Por favor contacta soporte en support@danasign.com para asistencia con la verificación de identidad.'
      })
    );
  }, [t]);

  /**
   * Handles errors during verification steps
   */
  const handleError = useCallback((error) => {
    console.error('Verification error:', error);
    setStatus('error');
    setMessage(
      error?.message ||
        t('verification.error-generic', {
          defaultValue: 'Ocurrió un error. Por favor intenta de nuevo.'
        })
    );
  }, [t]);

  useEffect(() => {
    if (step !== 'document' || status !== 'error' || !message) {
      return;
    }

    const contentContainer = modalContentRef.current;
    if (!contentContainer) {
      return;
    }

    contentContainer.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [step, status, message]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="verification-modal-overlay">
      <div className="verification-modal">
        {/* Header - only show on privacy step */}
        {step === 'privacy' && (
          <div className="verification-modal-header">
            <h2 className="text-xl font-bold text-gray-900">
              {t('verification.modal-title', {
                defaultValue: 'Verificación de identidad requerida'
              })}
            </h2>
          </div>
        )}

        {/* Progress indicator */}
        <div className="verification-progress">
          <div className="verification-progress-bar">
            <div
              className="verification-progress-fill"
              style={{
                width:
                  step === 'privacy'
                    ? '25%'
                    : step === 'document'
                    ? '50%'
                    : step === 'selfie'
                    ? '75%'
                    : '100%'
              }}
            />
          </div>
          <div className="verification-progress-label">
            {step === 'privacy' && t('verification.step-privacy', { defaultValue: 'Paso 1 de 4: Aviso de Privacidad' })}
            {step === 'document' && t('verification.step-document', { defaultValue: 'Paso 2 de 4: Captura de Documento' })}
            {step === 'selfie' && t('verification.step-selfie', { defaultValue: 'Paso 3 de 4: Selfie y Vida' })}
            {step === 'result' && t('verification.step-result', { defaultValue: 'Paso 4 de 4: Resultado' })}
          </div>
        </div>

        {/* Content */}
        <div ref={modalContentRef} className={`verification-modal-content ${step === 'selfie' ? 'selfie-step' : ''}`}>
          {/* Status message */}
          {message && status !== 'idle' && (
            <div
              className={`amplify-alert ${
                status === 'success'
                  ? 'amplify-alert--success'
                  : status === 'error'
                  ? 'amplify-alert--error'
                  : 'amplify-alert--info'
              } mb-4`}
            >
              <span>{message}</span>
              {status === 'error' && step === 'document' && (
                <button
                  type="button"
                  className="amplify-button amplify-button--small amplify-button--outlined mt-2 w-full"
                  onClick={() => {
                    setDocumentFront(null);
                    setDocumentBack(null);
                    setStatus('idle');
                    setMessage('');
                  }}
                >
                  {t('verification.retry-document', { defaultValue: 'Intentar con otro documento' })}
                </button>
              )}
            </div>
          )}

          {/* Loading indicator */}
          {status === 'processing' && (
            <div className="flex justify-center my-4">
              <div className="amplify-loader" />
            </div>
          )}

          {/* Step content */}
          {step === 'privacy' && <PrivacyStep onContinue={handlePrivacyContinue} />}

          {step === 'document' && (
            <DocumentCaptureStep
              documentFront={documentFront}
              documentBack={documentBack}
              onDocumentFrontCapture={setDocumentFront}
              onDocumentBackCapture={setDocumentBack}
              onError={handleError}
              requireDocumentBack={isDocumentBackRequired}
              isProcessing={status === 'processing'}
            />
          )}

          {step === 'selfie' && (
            <SelfieWithLivenessStep
              onCapture={handleSelfieCapture}
              onBack={() => setStep('document')}
              isProcessing={status === 'processing'}
            />
          )}

          {step === 'result' && (
            <ResultStep
              result={result}
              onContinue={handleResultContinue}
              onRetry={handleRetry}
              onContactSupport={handleContactSupport}
            />
          )}
        </div>

        {/* Fixed footer with Continue button - visible on privacy and document steps (when all captures are done) */}
        {(step === 'privacy' || (step === 'document' && (isDocumentBackRequired ? (documentFront && documentBack) : documentFront))) && (
          <div className="verification-modal-footer">
            <button
              type="button"
              className="amplify-button amplify-button--primary w-full py-3"
              disabled={status === 'processing'}
              onClick={step === 'privacy' ? handlePrivacyContinue : async () => {
                const allCaptured = isDocumentBackRequired
                  ? (documentFront && documentBack)
                  : documentFront;
                if (!allCaptured) return;
                try {
                  await validateCapturedDocumentExpiry(documentFront);
                  handleDocumentCapture(documentFront, documentBack);
                } catch (validationError) {
                  handleError(validationError);
                }
              }}
            >
              {status === 'processing'
                ? t('verification.checking-document-expiry', { defaultValue: 'Verificando...' })
                : t('verification.continue-button', { defaultValue: 'Continuar' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerificationModal;
